import {
  Flex,
  Grid,
  Stack,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { Field } from "../../../components/Form";
import { FormSection, FormSectionInner } from "../../../components/FormSection";
import { useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import CTA from "../../../components/Page/CTA";

// Helper function to strip HTML tags and get plain text for JSON-LD
const htmlToText = (html) => {
  if (!html) return "";
  // Create a temporary DOM element to parse HTML
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  // Get text content and clean up extra whitespace
  return tmp.textContent || tmp.innerText || "";
};

const StaticPageView = ({ data, deleteUser }) => {
  const { type } = useParams();

  const { setValue, register } = useFormContext();
  const faqItems = useWatch({
    name: "hero.[0].readmore.[1].faqs",
  });
  const faqHeading = useWatch({ name: "hero.[0].readmore.[1].heading" });
  useEffect(() => {
    type === "create" && setValue("type", "location");
  }, [type]);

  useEffect(() => {
    // ensure RHF knows about these programmatic-only fields
    register("hero.[0].readmore.[1].type");
    register("hero.[0].readmore.[1].jsonLd");
  }, [register]);

  useEffect(() => {
    // persist block type so backend can recognise this as FAQ object
    setValue("hero.[0].readmore.[1].type", "faqQuestionAnswe");
  }, [setValue]);

  useEffect(() => {
    if (!faqItems || faqItems.length === 0) {
      setValue("hero.[0].readmore.[1].jsonLd", "");
      return;
    }

    const mainEntity =
      faqItems
        ?.filter((item) => item?.question && item?.answer)
        .map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: htmlToText(item.answer), // Clean HTML to plain text for JSON-LD
          },
        })) || [];

    const jsonLd = JSON.stringify(
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        name: faqHeading,
        mainEntity,
      },
      null,
      2
    );

    setValue("hero.[0].readmore.[1].jsonLd", jsonLd);
  }, [faqItems, faqHeading, setValue]);

  return (
    <Flex direction="column" maxWidth="75%" w="100%">
      <Tabs
        orientation="vertical"
        w="100%"
        m="0"
        p="0"
        ml="-40px"
        align="start"
      >
        <TabList>
          <Tab justifyContent={"flexStart"}>General</Tab>
          <Tab justifyContent={"flexStart"}>Content</Tab>
          <Tab justifyContent={"flexStart"}>Advertising</Tab>

          <Tab justifyContent={"flexStart"}>Seo</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Grid
              templateColumns={{ base: "1fr" }}
              w="100%"
              maxWidth="1000px"
              gap="25px"
            >
              <Stack spacing="20px" w="100%">
                <FormSection title="General Details">
                  <FormSectionInner columns={1}>
                    <Field
                      component="text"
                      name="title"
                      placeholder="Name"
                      rules={{ required: true }}
                    />
                    <Field
                      name="hero[0].image"
                      placeholder="Image"
                      component="file"
                    />
                    <Field
                      imageType="thumbnail"
                      name="hero[0].thumbnail"
                      placeholder="Thumbnail"
                      component="file"
                    />
                  </FormSectionInner>
                </FormSection>
                {/* <FormSectionInner columns={1}>
                    <Field component='text' name='url' placeholder='URL' />
                  </FormSectionInner> */}
                <FormSection title={"Hero"}>
                  <FormSectionInner columns={1}>
                    {/* <Field
                      name='hero.[0].description'
                      component='page'
                      rules={{ required: true }}
                      placeholder='Description'
                    /> */}
                    <Field
                      name="hero.[0].description.[0].heading"
                      component="text"
                      rules={{ required: true }}
                      placeholder="Heading"
                    />
                    <Field
                      name="hero.[0].description.[0].text"
                      component="richtext"
                      defaultValue={data?.hero?.[0]?.description?.[0]?.text}
                      rules={{ required: true }}
                      placeholder="Description"
                    />
                    <Field
                      name="hero.[0].description.[0].type"
                      component="hidden"
                      value="textWithHeading"
                      placeholder=""
                    />
                  </FormSectionInner>
                  <FormSectionInner columns={1}>
                    <Field
                      name="hero.[0].readmore.[0].heading"
                      component="text"
                      rules={{ required: true }}
                      placeholder="Read more heading"
                    />
                    <Field
                      name="hero.[0].readmore.[0].text"
                      component="richtext"
                      defaultValue={data?.hero?.[0]?.readmore?.[0]?.text}
                      rules={{ required: true }}
                      placeholder="Read more description"
                    />
                    <Field
                      name="hero.[0].readmore.[0].type"
                      component="hidden"
                      value="textWithHeading"
                    />
                  </FormSectionInner>
                </FormSection>
                <FormSection title="FAQs">
                  <FormSectionInner columns={1}>
                    <Field
                      name="hero.[0].readmore.[1].heading"
                      component="text"
                      placeholder="FAQ heading"
                      label="Heading"
                    />
                    <Field
                      name="hero.[0].readmore.[1].faqs"
                      component="repeater"
                      singleName="FAQ"
                      layout="vertical"
                      width="100%"
                      minWidth="100%"
                      label="FAQs"
                      isRepeater={true}
                      fieldArray={[
                        {
                          name: "question",
                          component: "text",
                          placeholder: "Question",
                          label: "Question",
                          rules: { required: true },
                        },
                        {
                          name: "answer",
                          component: "richtext",
                          placeholder: "Answer",
                          label: "Answer",
                          rules: { required: true },
                        },
                      ]}
                    />
                    <Field
                      name="hero.[0].readmore.[1].type"
                      component="hidden"
                      value="faqQuestionAnswe"
                    />
                  </FormSectionInner>
                </FormSection>
                <CTA />
              </Stack>
            </Grid>
          </TabPanel>
          <TabPanel>
            <FormSectionInner columns={1}>
              <Field
                name="content"
                component="page"
                blog={true}
                location={true}
                gallery={true}
              />
            </FormSectionInner>
          </TabPanel>
          <TabPanel>
            <AdvertComponent />
          </TabPanel>
          <TabPanel>
            <Flex mb="20px">
              <FormSection title="Seo">
                <FormSectionInner columns={1}>
                  <Field
                    component="text"
                    name="seo.title"
                    placeholder="SEO Title"
                  />
                  <Field
                    component="text"
                    name="seo.description"
                    placeholder="SEO Description"
                  />
                  <Field component="text" name="slug" placeholder="Page slug"  rules={{ required: true }}/>
                </FormSectionInner>
              </FormSection>
            </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

const AdvertComponent = () => {
  return (
    <FormSection title="Advertise">
      <FormSectionInner columns={1}>
        {" "}
        <Field
          name="cta.adspace.active"
          component="switch"
          placeholder="Enable Ad?"
        />
        <Field name="hero[0].image" placeholder="Image" component="file" />
        <Field
          imageType="thumbnail"
          name="hero[0].thumbnail"
          placeholder="Thumbnail"
          component="file"
        />
        <Field
          name="cta.adspace.heading"
          component="text"
          placeholder="Advert heading"
        />
        <Field
          name="cta.adspace.subheading"
          component="text"
          placeholder="Sub-heading"
        />
        <Field
          name="cta.adspace.link"
          component="text"
          placeholder="Link url"
        />
      </FormSectionInner>
    </FormSection>
  );
};

export default StaticPageView;
